import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { systemNotificationService, type SystemNotification } from '../services/systemNotificationService'
import { 
  BellIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

const AdminNotificationsPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const [notifications, setNotifications] = useState<SystemNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingNotification, setEditingNotification] = useState<SystemNotification | null>(null)
  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    type: 'info' as const,
    priority: 'medium' as const,
    targetAudience: 'all' as const,
    expiresAt: ''
  })
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadNotifications()
  }, [])

  const loadNotifications = async () => {
    setLoading(true)
    try {
      const result = await systemNotificationService.getSystemNotifications()
      if (result.success && result.notifications) {
        setNotifications(result.notifications)
      } else {
        showError('Error', result.error || 'Failed to load notifications')
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
      showError('Error', 'Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateNotification = async () => {
    if (!user) return
    
    setCreating(true)
    try {
      const result = await systemNotificationService.createSystemNotification({
        title: newNotification.title,
        message: newNotification.message,
        type: newNotification.type,
        priority: newNotification.priority,
        targetAudience: newNotification.targetAudience,
        expiresAt: newNotification.expiresAt || undefined,
        createdBy: `${user.firstName} ${user.lastName}` || user.email
      })
      
      if (result.success && result.notification) {
        setNotifications(prev => [result.notification!, ...prev])
        setShowCreateModal(false)
        setNewNotification({
          title: '',
          message: '',
          type: 'info',
          priority: 'medium',
          targetAudience: 'all',
          expiresAt: ''
        })
        showSuccess('Success', 'Notification created and sent to target users!')
      } else {
        showError('Error', result.error || 'Failed to create notification')
      }
    } catch (error) {
      console.error('Error creating notification:', error)
      showError('Error', 'Failed to create notification')
    } finally {
      setCreating(false)
    }
  }

  const handleToggleActive = async (id: string) => {
    const notification = notifications.find(n => n.id === id)
    if (!notification) return

    try {
      const result = await systemNotificationService.toggleSystemNotification(id, !notification.isActive)
      if (result.success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === id ? { ...notif, isActive: !notif.isActive } : notif
          )
        )
        showSuccess('Success', `Notification ${!notification.isActive ? 'activated' : 'deactivated'}`)
      } else {
        showError('Error', result.error || 'Failed to update notification')
      }
    } catch (error) {
      console.error('Error toggling notification:', error)
      showError('Error', 'Failed to update notification')
    }
  }

  const handleDeleteNotification = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this notification? This action cannot be undone.')) {
      try {
        const result = await systemNotificationService.deleteSystemNotification(id)
        if (result.success) {
          setNotifications(prev => prev.filter(notif => notif.id !== id))
          showSuccess('Success', 'Notification deleted')
        } else {
          showError('Error', result.error || 'Failed to delete notification')
        }
      } catch (error) {
        console.error('Error deleting notification:', error)
        showError('Error', 'Failed to delete notification')
      }
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return InformationCircleIcon
      case 'warning': return ExclamationTriangleIcon
      case 'error': return XCircleIcon
      case 'success': return CheckCircleIcon
      default: return BellIcon
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'text-blue-400 bg-blue-500/20 border-blue-500/30'
      case 'warning': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30'
      case 'error': return 'text-red-400 bg-red-500/20 border-red-500/30'
      case 'success': return 'text-green-400 bg-green-500/20 border-green-500/30'
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-400 bg-red-500/20'
      case 'high': return 'text-orange-400 bg-orange-500/20'
      case 'medium': return 'text-yellow-400 bg-yellow-500/20'
      case 'low': return 'text-green-400 bg-green-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const notificationStats = {
    total: notifications.length,
    active: notifications.filter(n => n.isActive).length,
    critical: notifications.filter(n => n.priority === 'critical').length,
    expired: notifications.filter(n => n.expiresAt && new Date(n.expiresAt) < new Date()).length
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Notifications Management" subtitle="Manage system notifications and alerts">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <BellIcon className="w-8 h-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{notificationStats.total}</span>
            </div>
            <div className="text-gray-400 text-sm">Total Notifications</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-400" />
              <span className="text-2xl font-bold text-white">{notificationStats.active}</span>
            </div>
            <div className="text-gray-400 text-sm">Active</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
              <span className="text-2xl font-bold text-white">{notificationStats.critical}</span>
            </div>
            <div className="text-gray-400 text-sm">Critical Priority</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <ClockIcon className="w-8 h-8 text-orange-400" />
              <span className="text-2xl font-bold text-white">{notificationStats.expired}</span>
            </div>
            <div className="text-gray-400 text-sm">Expired</div>
          </div>
        </motion.div>

        {/* Header with Create Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">System Notifications</h1>
            <p className="text-gray-400">Create and manage notifications for users and admins</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={async () => {
                if (!user) return
                try {
                  const result = await systemNotificationService.sendMaintenanceNotification(
                    'Scheduled Maintenance',
                    'System maintenance will occur this Sunday from 2:00 AM to 4:00 AM EST. Some features may be temporarily unavailable during this time.'
                  )
                  if (result.success) {
                    showSuccess('Success', 'Maintenance notification sent to all users!')
                    loadNotifications() // Refresh the list
                  } else {
                    showError('Error', result.error || 'Failed to send maintenance notification')
                  }
                } catch (error) {
                  showError('Error', 'Failed to send maintenance notification')
                }
              }}
              className="flex items-center space-x-2 px-4 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-medium transition-all"
            >
              <ExclamationTriangleIcon className="w-5 h-5" />
              <span>Send Maintenance Alert</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Create Notification</span>
            </button>
          </div>
        </motion.div>

        {/* Notifications List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {loading ? (
            <div className="glass-mockup rounded-2xl p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="glass-mockup rounded-2xl p-8 text-center">
              <BellIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No notifications found</p>
            </div>
          ) : (
            notifications.map((notification, index) => {
              const TypeIcon = getTypeIcon(notification.type)
              const isExpired = notification.expiresAt && new Date(notification.expiresAt) < new Date()
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-mockup rounded-2xl p-6 border ${getTypeColor(notification.type)} ${!notification.isActive ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-gray-500 to-slate-500 flex items-center justify-center">
                        <TypeIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-bold text-lg">{notification.title}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${notification.isActive ? 'text-green-400 bg-green-500/20' : 'text-gray-400 bg-gray-500/20'}`}>
                            {notification.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {isExpired && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-red-400 bg-red-500/20">
                              Expired
                            </span>
                          )}
                        </div>
                        <p className="text-gray-300 mb-3">{notification.message}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <div className="flex items-center space-x-1">
                            <UserGroupIcon className="w-4 h-4" />
                            <span>Target: {notification.targetAudience}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ClockIcon className="w-4 h-4" />
                            <span>Created: {new Date(notification.createdAt).toLocaleDateString()}</span>
                          </div>
                          {notification.expiresAt && (
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>Expires: {new Date(notification.expiresAt).toLocaleDateString()}</span>
                            </div>
                          )}
                          <span>By: {notification.createdBy}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(notification.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          notification.isActive 
                            ? 'text-green-400 hover:bg-green-500/20' 
                            : 'text-gray-400 hover:bg-gray-500/20'
                        }`}
                        title={notification.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <CheckCircleIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setEditingNotification(notification)}
                        className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )
            })
          )}
        </motion.div>
      </main>

      {/* Create/Edit Notification Modal */}
      {(showCreateModal || editingNotification) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingNotification ? 'Edit Notification' : 'Create New Notification'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditingNotification(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-white font-medium block mb-2">Title</label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  placeholder="Enter notification title"
                />
              </div>

              <div>
                <label className="text-white font-medium block mb-2">Message</label>
                <textarea
                  value={newNotification.message}
                  onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all resize-none"
                  placeholder="Enter notification message"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium block mb-2">Type</label>
                  <select
                    value={newNotification.type}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, type: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  >
                    <option value="info" className="bg-gray-800">Info</option>
                    <option value="warning" className="bg-gray-800">Warning</option>
                    <option value="error" className="bg-gray-800">Error</option>
                    <option value="success" className="bg-gray-800">Success</option>
                  </select>
                </div>

                <div>
                  <label className="text-white font-medium block mb-2">Priority</label>
                  <select
                    value={newNotification.priority}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  >
                    <option value="low" className="bg-gray-800">Low</option>
                    <option value="medium" className="bg-gray-800">Medium</option>
                    <option value="high" className="bg-gray-800">High</option>
                    <option value="critical" className="bg-gray-800">Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium block mb-2">Target Audience</label>
                  <select
                    value={newNotification.targetAudience}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, targetAudience: e.target.value as any }))}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  >
                    <option value="all" className="bg-gray-800">All Users</option>
                    <option value="admins" className="bg-gray-800">Admins Only</option>
                    <option value="users" className="bg-gray-800">Regular Users</option>
                    <option value="specific" className="bg-gray-800">Specific Users</option>
                  </select>
                </div>

                <div>
                  <label className="text-white font-medium block mb-2">Expires At (Optional)</label>
                  <input
                    type="datetime-local"
                    value={newNotification.expiresAt}
                    onChange={(e) => setNewNotification(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditingNotification(null)
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNotification}
                disabled={creating || !newNotification.title || !newNotification.message}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-all"
              >
                {creating ? 'Creating...' : editingNotification ? 'Update' : 'Create'} Notification
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default AdminNotificationsPage 